/**
 * InitialSyncBoundary — shown ONLY to brand-new users who have never synced
 * (lastSyncedAt === null) and whose first initSync has not yet completed.
 *
 * Returning users (lastSyncedAt !== null) bypass this entirely — they always
 * see their cached local data immediately.
 *
 * M81 Phase 5b: When currentEntity === 'photos', renders a "Restoring photos…"
 * sub-message beneath the spinner. Two variants:
 *   - count variant (>= PHOTO_HYDRATION_COUNT_THRESHOLD): "Restoring N photos…"
 *   - no-count variant (> 0 but < threshold): "Restoring photos…"
 *   - neither variant when currentEntity !== 'photos'
 *
 * J-10: Visual weight upgrade — logo 2×, indeterminate progress bar, display-sm
 * primary text. All sizing constants are provisional pending Gemini consultation.
 */

import type { ReactNode } from 'react';

import wordmarkStacked from '@/assets/logo/wordmark-stacked.svg';
import { useSyncStore } from '@/stores/sync-store';

// ── J-10 Visual constants (RULE-153) ─────────────────────────────────────────
// All values are provisional — pending Gemini Korva Design Gem consultation.
// See docs/pipeline/j-10-gemini-consultation.md

/** Logo height in pixels (provisional — pending Gemini design review) */
export const INITIAL_SYNC_LOGO_SIZE_PX = 96; // provisional — pending Gemini design review

/** Progress bar height in pixels (provisional — pending Gemini design review) */
export const INITIAL_SYNC_PROGRESS_HEIGHT_PX = 6; // provisional — pending Gemini design review

/** Progress bar max-width in pixels (provisional — pending Gemini design review) */
export const INITIAL_SYNC_PROGRESS_WIDTH_PX = 240; // provisional — pending Gemini design review

/** Primary message text size token (provisional — pending Gemini design review) */
export const INITIAL_SYNC_PRIMARY_TEXT_SIZE = 'display-sm'; // provisional — pending Gemini design review

/** Sub-message text size token (provisional — pending Gemini design review) */
export const INITIAL_SYNC_SUB_TEXT_SIZE = 'body-md'; // provisional — pending Gemini design review

/**
 * Minimum photo count required to show the numeric count in the sub-message.
 * Below this threshold, "Restoring photos…" is shown without a number
 * to avoid noisy "Restoring 1 photo…" messages on incremental hydration.
 * (RULE-153 named constant)
 */
export const PHOTO_HYDRATION_COUNT_THRESHOLD = 5;

interface InitialSyncBoundaryProps {
  children: ReactNode;
}

export function InitialSyncBoundary({ children }: InitialSyncBoundaryProps) {
  const { lastSyncedAt, isInitialSyncComplete, currentEntity, photoHydrationCount } =
    useSyncStore();

  // Returning user: has prior sync data — show app immediately
  if (lastSyncedAt !== null) {
    return <>{children}</>;
  }

  // First-time user: still waiting for initial sync to complete
  if (!isInitialSyncComplete) {
    // Determine photo hydration sub-message (M81 Phase 5b, RULE-149 three branches)
    const showPhotoMessage = currentEntity === 'photos' && photoHydrationCount > 0;
    const showCountVariant =
      showPhotoMessage && photoHydrationCount >= PHOTO_HYDRATION_COUNT_THRESHOLD;

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-8"
        data-testid="initial-sync-boundary"
        data-layout="centered"
        role="progressbar"
        aria-busy="true"
        aria-label="Setting up your account"
      >
        {/* Single flat column — explicit gap between each pair (J-10 spec, RULE-153):
            logo→primary: gap-8, primary→bar: gap-4, bar→sub: gap-3 */}
        <div className="flex flex-col items-center">
          {/* Logo — 2× scale per J-10 (provisional) */}
          <img
            src={wordmarkStacked}
            alt="Ridgen"
            className="w-auto"
            style={{ height: INITIAL_SYNC_LOGO_SIZE_PX }}
            data-logo-size="display"
          />

          {/* gap-8 logo → primary (J-10 spec) */}
          {/* Primary message — display-sm per J-10 (provisional) */}
          {/* NOTE: Tailwind class is literal below — INITIAL_SYNC_PRIMARY_TEXT_SIZE='display-sm' matches text-display-sm */}
          <p
            className="mt-8 text-display-sm text-[var(--color-text-primary)] text-center"
            data-message-role="primary"
          >
            Setting up your account…
          </p>

          {/* gap-4 primary → bar (J-10 spec) */}
          {/* Indeterminate sliding progress bar (J-10, provisional) */}
          <div
            className="mt-4 overflow-hidden rounded-full bg-bark-700"
            style={{
              height: INITIAL_SYNC_PROGRESS_HEIGHT_PX,
              maxWidth: INITIAL_SYNC_PROGRESS_WIDTH_PX,
              width: '100%',
            }}
            aria-hidden="true"
          >
            <div
              className="h-full rounded-full bg-ember-500 animate-progress-slide"
              style={{ width: '30%' }}
            />
          </div>

          {/* gap-3 bar → sub-message (J-10 spec) */}
          {/* Photo hydration sub-message (three RULE-149 branches via data-hydration-state) */}
          {/* NOTE: sub-message uses text-body-md — INITIAL_SYNC_SUB_TEXT_SIZE='body-md' */}
          {showCountVariant && (
            <p
              className="mt-3 text-body-md text-bark-400 text-center"
              data-testid="photo-hydration-message"
              data-hydration-state="count-variant"
              data-message-role="secondary"
            >
              Restoring {photoHydrationCount} photos…
            </p>
          )}
          {showPhotoMessage && !showCountVariant && (
            <p
              className="mt-3 text-body-md text-bark-400 text-center"
              data-testid="photo-hydration-message"
              data-hydration-state="no-count-variant"
              data-message-role="secondary"
            >
              Restoring photos…
            </p>
          )}
          {!showPhotoMessage && (
            <span
              data-testid="photo-hydration-message"
              data-hydration-state="absent"
              data-message-role="secondary"
              className="sr-only"
              aria-hidden
            />
          )}
        </div>
      </div>
    );
  }

  // Initial sync complete (success or error) — let user through
  return <>{children}</>;
}
